import { useMediaQuery } from "@mui/material";
import { Navigation } from "interfaces";
import { useRef } from "react";
import { useLocation } from "react-router-dom";
import { Navigation as SwiperNavigation } from "swiper/modules";
import type { Swiper as SwiperClass } from "swiper/types";
import { Swiper, SwiperSlide } from "swiper/react";
import { useTutorialSlide } from "../hooks/usetutorialSlide";
import { MenuOption } from "./MenuOption";
import { CustomBox } from "./styled";
import { getSwiperSlideSpeed } from "utils/getSwiperSlideSpeed";
import { isPageActive } from "helpers/isPageActive";

// Persists across mounts (each page re-mounts this component) so the swiper
// can start from the tab the user came from instead of always from slide 0.
let lastActivePagePath: string | null = null;

const MainMenu = ({
	pages
}: {
	pages: Navigation[];
	className?: string;
	isLandingPage?: boolean;
}): JSX.Element => {
	const isLowWidth = useMediaQuery("(max-width:944px)");
	const swiperInstance = useRef<SwiperClass>();
	const location = useLocation();

	// Changing this remounts the Swiper below from scratch whenever the tab
	// list itself changes (e.g. a tab appearing behind an async feature
	// flag), so it re-measures and re-positions exactly the way it already
	// does on a real page navigation, instead of patching a live instance.
	const pagesKey = pages.map((page) => page.path).join("|");

	useTutorialSlide({ pages, onGoToSlide: (n) => goToSlide(n) });

	const goToSlide = (index: number) => {
		//swiperInstance?.slideTo(index);
		const currentIndex = swiperInstance?.current?.activeIndex ?? index;
		swiperInstance?.current?.slideTo(
			index,
			getSwiperSlideSpeed(index - currentIndex),
			false
		);
	};

	return (
		<CustomBox sx={{ paddingTop: { xs: "4px", md: "14px" } }}>
			<Swiper
				key={pagesKey}
				//onSwiper={setSwiperInstance}
				style={{ height: isLowWidth ? "45px" : "68px" }}
				onInit={(swiper) => {
					swiperInstance.current = swiper; // Aquí almacenamos la instancia de swiper en ref

					const activeIndex = pages.findIndex((page) =>
						isPageActive(location.pathname, page)
					);
					if (activeIndex === -1) {
						// The current route's tab isn't in `pages` yet (e.g. behind an
						// async feature flag). Track the real route anyway so that once
						// it appears and the Swiper below remounts (key change), it
						// starts from here instead of a stale path from a previous mount.
						lastActivePagePath = location.pathname;
						return;
					}

					const previousIndex = pages.findIndex(
						(page) => page.path === lastActivePagePath
					);
					const startIndex = previousIndex === -1 ? activeIndex : previousIndex;

					// Snap instantly to where the user last was (no visible jump,
					// since this is the swiper's very first paint), then let the
					// browser commit that frame before animating to the new tab —
					// doing both in the same tick made the transition skip
					// straight to the final position when moving to a lower index.
					swiper.slideTo(startIndex, 0, false);
					if (startIndex !== activeIndex) {
						const speed = getSwiperSlideSpeed(activeIndex - startIndex);
						requestAnimationFrame(() => {
							swiper.slideTo(activeIndex, speed, false);
						});
					}
					lastActivePagePath = pages[activeIndex].path;
				}}
				onSlideChange={(swiper: SwiperClass) => {
					// Only fires for manual drags: our own programmatic slideTo
					// calls above pass runCallbacks=false. Keeps the persisted
					// position in sync with wherever the user actually scrolled to,
					// so we don't re-animate from a stale position next mount.
					const page = pages[swiper.activeIndex];
					if (page) {
						lastActivePagePath = page.path;
					}
				}}
				modules={[SwiperNavigation]}
				slidesPerView={"auto"}
				slidesOffsetBefore={35}
				slidesOffsetAfter={35}
				spaceBetween={8}
				threshold={5}
				freeMode={false}
				loop={false}
				pagination={{
					clickable: true
					//el: ".swiper-custom-pagination"
				}}
				className="mySwiper"
				//modules={[Pagination, FreeMode]}
			>
				{pages.map((page) => {
					return (
						<SwiperSlide key={page.path} style={{ width: "auto" }}>
							<MenuOption page={page}></MenuOption>
						</SwiperSlide>
					);
				})}
			</Swiper>
			{/* <button onClick={() => goToSlide(5)}>Test</button> */}
		</CustomBox>
	);
};

export default MainMenu;
